import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  GuaranteesBeneficiarySanctionsStatus,
  type TGuaranteesApplication,
  type TGuaranteesManagerTerms,
  type TPatchGuaranteesManagerTermsPayload,
} from '~/store/guarantees/types.ts';
import {
  GuaranteesManagerTermsFieldName,
  type TGuaranteesManagerTermsFormValues,
} from './types.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import { BENEFICIARY_SANCTIONS_STATUS_OPTIONS } from './constants.ts';

const beneficiarySanctionsStatusValues = Object.values(GuaranteesBeneficiarySanctionsStatus);

const isBeneficiarySanctionsStatus = (
  value?: string | null
): value is GuaranteesBeneficiarySanctionsStatus =>
  beneficiarySanctionsStatusValues.includes(value as GuaranteesBeneficiarySanctionsStatus);

const getBeneficiarySanctionsStatusOption = (
  value?: string | null
): TAutocompleteOption | null => {
  const normalizedValue = getStringValue(value);

  return (
    BENEFICIARY_SANCTIONS_STATUS_OPTIONS.find(
      (option) => option.value === normalizedValue
    ) ?? null
  );
};

export const getGuaranteesManagerTermsValidationSchema = (
  isSanctionsStatusRequired: boolean
) =>
  yup.object({
    [GuaranteesManagerTermsFieldName.BeneficiarySanctionsStatus]: yup
      .mixed<TAutocompleteOption>()
      .nullable()
      .test('beneficiary-sanctions-status-required', errorMessages.FIELD_REQUIRED, (value) => {
        if (!isSanctionsStatusRequired) {
          return true;
        }

        return isBeneficiarySanctionsStatus(value?.value);
      }),
    [GuaranteesManagerTermsFieldName.DebtType]: yup.string(),
  });

export const getGuaranteesManagerTermsInitialValues = (
  managerTerms?: TGuaranteesManagerTerms | null
): TGuaranteesManagerTermsFormValues => {
  const hasBeneficiarySanctionsMatch =
    managerTerms?.beneficiarySanctionsMatchDetected === true;
  return {
    [GuaranteesManagerTermsFieldName.BeneficiarySanctionsStatus]:
      hasBeneficiarySanctionsMatch
        ? getBeneficiarySanctionsStatusOption(managerTerms?.beneficiarySanctionsStatus)
        : null,
    [GuaranteesManagerTermsFieldName.DebtType]: getStringValue(managerTerms?.debtType),
  };
};

export const getGuaranteesManagerTermsFromPatchResponse = (
  response?: TGuaranteesManagerTerms | TGuaranteesApplication | null
): TGuaranteesManagerTerms | null => {
  if (!response) {
    return null;
  }

  if ('managerTerms' in response && response.managerTerms) {
    return response.managerTerms;
  }

  if (
    'guaranteeExpiryDate' in response ||
    'beneficiarySanctionsMatchDetected' in response ||
    'beneficiarySanctionsStatus' in response ||
    'debtType' in response
  ) {
    return response as TGuaranteesManagerTerms;
  }

  return null;
};

export const getGuaranteesManagerTermsResolver = (
  isSanctionsStatusRequired: boolean
): Resolver<TGuaranteesManagerTermsFormValues> =>
  yupResolver(
    getGuaranteesManagerTermsValidationSchema(isSanctionsStatusRequired)
  ) as unknown as Resolver<TGuaranteesManagerTermsFormValues>;

export const buildGuaranteesManagerTermsPayload = (
  values: TGuaranteesManagerTermsFormValues,
  hasBeneficiarySanctionsMatch: boolean
): TPatchGuaranteesManagerTermsPayload => ({
  beneficiarySanctionsStatus:
    hasBeneficiarySanctionsMatch &&
    isBeneficiarySanctionsStatus(values.beneficiarySanctionsStatus?.value)
      ? values.beneficiarySanctionsStatus.value
      : null,
});
